'use client';

import React, { useState, useEffect } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import styles from './AYCECaseStudy.module.css';

interface Integration {
  name: string;
  logo: string;
  category: string;
}

// Device pair interface - QR + Tablet together
interface DevicePair {
  qr: string;
  tablet: string;
}

interface AYCECaseStudyItem {
  company: string;
  solution: string;
  images: string[]; // Array of ALL images (both QR and Tablet mixed)
  description: string;
  integrations: Integration[];
  color?: string; // Brand color for glow effect
}

export interface AYCECaseStudyProps {
  badge: string;
  title: string;
  subtitle: string;
  cases: AYCECaseStudyItem[];
  labels: {
    client: string;
    solution: string;
    integratedWith: string;
  };
  altTexts?: Record<string, string>;
}

/**
 * AYCECaseStudy Component
 * Displays All You Can Eat case studies with QR + Tablet device combos
 * Shows both devices together like in the hero section
 */
export function AYCECaseStudy({ badge, title, subtitle, cases, labels, altTexts }: AYCECaseStudyProps) {
  const [activePairIndices, setActivePairIndices] = useState<Record<number, number>>({});
  const [isVisible, setIsVisible] = useState(false);

  // Helper function to pair QR and Tablet images
  const getDevicePairs = (images: string[]): DevicePair[] => {
    const pairs: DevicePair[] = [];
    const qrImages = images.filter(img => img.includes('-qr-'));
    const tabletImages = images.filter(img => img.includes('-tablet-'));
    
    const maxPairs = Math.min(qrImages.length, tabletImages.length);
    for (let i = 0; i < maxPairs; i++) {
      pairs.push({
        qr: qrImages[i],
        tablet: tabletImages[i]
      });
    }
    
    return pairs;
  };

  // Auto-rotate device pairs every 4 seconds
  useEffect(() => {
    const intervals: NodeJS.Timeout[] = [];

    cases.forEach((caseItem, caseIndex) => {
      const pairs = getDevicePairs(caseItem.images);
      if (pairs.length > 1) {
        const interval = setInterval(() => {
          setActivePairIndices((prev) => ({
            ...prev,
            [caseIndex]: ((prev[caseIndex] || 0) + 1) % pairs.length,
          }));
        }, 4000);
        intervals.push(interval);
      }
    });

    return () => {
      intervals.forEach((interval) => clearInterval(interval));
    };
  }, [cases]);

  // Lazy loading observer
  useEffect(() => {
    if (!isVisible) {
      const observer = new IntersectionObserver(
        (entries) => {
          if (entries[0].isIntersecting) {
            setIsVisible(true);
          }
        },
        { rootMargin: '400px' }
      );

      const element = document.getElementById('ayce-case-study');
      if (element) {
        observer.observe(element);
      }

      return () => {
        if (element) {
          observer.unobserve(element);
        }
      };
    }
  }, [isVisible]);

  return (
    <section id="ayce-case-study" className={styles.caseStudy}>
      <div className={styles.container}>
        <div className={styles.header}>
          <div className={styles.badge}>{badge}</div>
          <h2 className={styles.title}>{title}</h2>
          <p className={styles.subtitle}>{subtitle}</p>
        </div>

        {cases.map((caseItem, caseIndex) => {
          const devicePairs = getDevicePairs(caseItem.images);
          const activePairIndex = activePairIndices[caseIndex] || 0;
          const activePair = devicePairs[activePairIndex];
          const brandColor = caseItem.color || '#ff6b35';

          return (
            <div key={caseIndex} className={`${styles.caseItem} ${caseIndex % 2 === 1 ? styles.reversed : ''}`}>
              <div className={styles.textContent}>
                <div className={styles.company}>
                  <span className={styles.companyLabel}>{labels.client}</span>
                  <span className={styles.companyName}>{caseItem.company}</span>
                </div>
                <div className={styles.solution}>
                  <span className={styles.solutionLabel}>{labels.solution}</span>
                  <span className={styles.solutionName}>{caseItem.solution}</span>
                </div>
                
                <div className={styles.descriptionWrapper}>
                  <p className={styles.description}>{caseItem.description}</p>
                  
                  {caseItem.integrations && caseItem.integrations.length > 0 && (
                    <div className={styles.integrations}>
                      <h4 className={styles.integrationsTitle}>{labels.integratedWith}</h4>
                      <div className={styles.integrationLogos}>
                        {caseItem.integrations.map((integration, idx) => {
                          const slug = integration.name.toLowerCase().replace(/\s+/g, '-').replace(/\./g, '');
                          
                          // List of partners that should go to /partners/ instead of /integrations/
                          const partners = [
                            'konacom', 'possystems', 'cecon', 'janssens-kassasystemen', 'ms-pos',
                            'adnamics', 'upta', 'kassanet-pieterse', 'opsomer', 'evers-kassasystemen',
                            'stn', 'kassanet-nuus', 'bork', 'prestop', 'acrelec', 'qiox',
                            'van-hessen' // Van Hessen is also a partner
                          ];
                          
                          // Determine if this is a partner or a real integration
                          const isPartner = partners.includes(slug);
                          const integrationUrl = isPartner ? `/partners/${slug}` : `/integrations/${slug}`;
                          
                            return (
                            <Link key={idx} href={integrationUrl} className={styles.integrationLogo} prefetch={true}>
                              <Image
                                src={integration.logo}
                                alt={altTexts?.[integration.name.toLowerCase().replace(/\s+/g, '-')] || `${integration.name} integration`}
                                width={120}
                                height={60}
                                style={{ objectFit: 'contain' }}
                                quality={75}
                              />
                              <span className={styles.integrationName}>{integration.name}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Device Combo - QR + Tablet together */}
              {isVisible && activePair && (
                <div className={styles.deviceComboContainer}>
                  <div className={styles.deviceCombo}>
                    <div className={styles.deviceComboInner}>
                      {/* Glow effect */}
                      <div 
                        className={styles.deviceGlow}
                        style={{ background: `radial-gradient(circle at center, ${brandColor}40 0%, transparent 70%)` }}
                      />
                      
                      {/* Devices */}
                      <div className={styles.deviceWrapper}>
                        {/* Tablet (behind) */}
                        <div className={styles.tabletWrapper}>
                          <Image
                            src={activePair.tablet}
                            alt={`${caseItem.company} Tablet ordering - ${activePairIndex + 1}`}
                            width={500}
                            height={800}
                            className={styles.tabletImage}
                            loading={caseIndex < 2 ? 'eager' : 'lazy'}
                            priority={caseIndex < 2}
                            quality={85}
                          />
                        </div>

                        {/* QR (in front) */}
                        <div className={styles.qrWrapper}>
                          <Image
                            src={activePair.qr}
                            alt={`${caseItem.company} QR ordering - ${activePairIndex + 1}`}
                            width={350}
                            height={700}
                            className={styles.qrImage}
                            loading={caseIndex < 2 ? 'eager' : 'lazy'}
                            priority={caseIndex < 2}
                            quality={85}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Carousel dots for multiple pairs */}
                    {devicePairs.length > 1 && (
                      <div className={styles.carouselDots}>
                        {devicePairs.map((_, idx) => (
                          <button
                            key={idx}
                            className={`${styles.dot} ${activePairIndex === idx ? styles.activeDot : ''}`}
                            onClick={() => setActivePairIndices((prev) => ({ ...prev, [caseIndex]: idx }))}
                            aria-label={`View device pair ${idx + 1}`}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

